import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Plus,
  Search,
  KanbanSquare,
  Archive,
  LayoutGrid,
  AlertCircle,
  CheckCircle2,
  Clock,
  Ban,
  Star,
  Activity,
  CalendarDays,
  Zap,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import { kanbanApiService, normalizeKanbanBoardFromApiResponse } from '../services/kanbanApi';
import { useKanbanTeamWebSocket } from '../hooks/useKanbanRealtime';
import { mergeTeamBoardsList } from '../utils/mergeKanbanRealtime';
import type { KanbanWsEnvelope } from '../types/kanbanRealtime';
import { PlayerProfile } from '../components/PlayerProfile';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { CreateBoardModal, type BoardCreatedContext } from '../components/kanban/CreateBoardModal';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { cn } from '../utils/cn';
import {
  kanbanTeamsFromPermissions,
  kanbanTeamsFromStaffTeams,
  mergeKanbanTeamLists,
} from '../utils/kanbanPermissions';
import {
  getLimitedTesseractTeamAccess,
  teamStaffOnlyKanbanRead,
} from '../utils/tesseractTeamAccess';

import type { KanbanBoard, KanbanStats, KanbanActivityLogEntry, KanbanUpcomingCadence } from '../types/kanban';
import { CADENCE_TYPE_CONFIG } from '../types/kanban';

/** Workspaces tab value: every board visible to the user (GET /kanban/boards with no team filter). */
const ALL_TEAMS_TAB = '__all__';

function mergeNumericRecords(
  into: Record<string, number>,
  from: Record<string, number> | undefined,
): void {
  if (!from) return;
  for (const [k, v] of Object.entries(from)) {
    into[k] = (into[k] ?? 0) + v;
  }
}

function aggregateKanbanStats(parts: KanbanStats[]): KanbanStats | null {
  if (parts.length === 0) return null;
  const cards_by_priority: Record<string, number> = {};
  const cards_by_type: Record<string, number> = {};
  const cards_by_class: Record<string, number> = {};
  let total_boards = 0;
  let total_lists = 0;
  let total_cards = 0;
  let total_archived_cards = 0;
  let cards_overdue = 0;
  let cards_blocked = 0;
  let cards_completed = 0;
  let total_comments = 0;
  let total_attachments = 0;
  let active_members = 0;

  for (const s of parts) {
    total_boards += s.total_boards;
    total_lists += s.total_lists;
    total_cards += s.total_cards;
    total_archived_cards += s.total_archived_cards;
    cards_overdue += s.cards_overdue;
    cards_blocked += s.cards_blocked;
    cards_completed += s.cards_completed;
    total_comments += s.total_comments;
    total_attachments += s.total_attachments;
    active_members += s.active_members;
    mergeNumericRecords(cards_by_priority, s.cards_by_priority);
    mergeNumericRecords(cards_by_type, s.cards_by_type);
    mergeNumericRecords(cards_by_class, s.cards_by_class);
  }

  return {
    total_boards,
    total_lists,
    total_cards,
    total_archived_cards,
    cards_by_priority,
    cards_by_type,
    cards_by_class,
    cards_overdue,
    cards_blocked,
    cards_completed,
    total_comments,
    total_attachments,
    active_members,
  };
}

export function WorkspacesView() {
  useDocumentTitle('Workspaces');

  const { identity, isLoading: authLoading } = useAuth();
  const isDev = import.meta.env.DEV;

  /** Raw list from `GET /kanban/teams` (often empty until a team has a board). */
  const [serverTeams, setServerTeams] = useState<string[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(ALL_TEAMS_TAB);
  const [boards, setBoards] = useState<KanbanBoard[]>([]);
  const [stats, setStats] = useState<KanbanStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [showArchived, setShowArchived] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingBoardId, setDeletingBoardId] = useState<string | null>(null);
  const [recentActivity, setRecentActivity] = useState<KanbanActivityLogEntry[]>([]);
  const [upcomingCadences, setUpcomingCadences] = useState<KanbanUpcomingCadence[]>([]);
  const [searchResults, setSearchResults] = useState<KanbanBoard[] | null>(null);
  const navigate = useNavigate();

  // Determine if user has manage permission (client-side heuristic)
  // canManage: true if the user can manage ANY team's boards (for showing Create Board)
  const jwtKanbanTeams = useMemo(() => {
    if (isDev) return [];
    return kanbanTeamsFromPermissions(identity?.raw?.custom?.permissions);
  }, [identity, isDev]);

  const staffKanbanTeams = useMemo(() => {
    if (isDev) return [];
    return kanbanTeamsFromStaffTeams(identity?.raw?.custom?.staff_teams);
  }, [identity, isDev]);

  const teams = useMemo(
    () => mergeKanbanTeamLists(mergeKanbanTeamLists(serverTeams, jwtKanbanTeams), staffKanbanTeams),
    [serverTeams, jwtKanbanTeams, staffKanbanTeams],
  );

  const canManage = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const permissions = Array.isArray(identity.raw?.custom?.permissions)
      ? identity.raw.custom.permissions
      : [];
    const groups = Array.isArray(identity.raw?.custom?.groups) ? identity.raw.custom.groups : [];
    if (groups.includes('SPY_Super_admin')) return true;
    if (permissions.includes('argus.kanban.super')) return true;
    return permissions.some((p: string) => p.startsWith('argus.kanban.manage.'));
  }, [identity, isDev]);

  const canReadTeam = useCallback(
    (team: string | null) => {
      if (!team) return false;
      if (isDev) return true;
      if (!identity) return false;
      if (teamStaffOnlyKanbanRead(identity, isDev, team)) return true;
      const permissions = Array.isArray(identity.raw?.custom?.permissions)
        ? identity.raw.custom.permissions
        : [];
      const groups = Array.isArray(identity.raw?.custom?.groups) ? identity.raw.custom.groups : [];
      if (groups.includes('SPY_Super_admin')) return true;
      if (permissions.includes('argus.kanban.super')) return true;
      return permissions.some(
        (p: string) =>
          p === `argus.kanban.read.${team}` ||
          p === `argus.kanban.write.${team}` ||
          p === `argus.kanban.manage.${team}`,
      );
    },
    [identity, isDev],
  );

  // Determine which teams the user can manage (for create board team dropdown)
  const limitedTesseract = useMemo(
    () => getLimitedTesseractTeamAccess(identity, isDev),
    [identity, isDev],
  );

  const manageableTeams = useMemo(() => {
    if (isDev) return teams;
    if (!identity) return [];
    const permissions = Array.isArray(identity.raw?.custom?.permissions)
      ? identity.raw.custom.permissions
      : [];
    const groups = Array.isArray(identity.raw?.custom?.groups) ? identity.raw.custom.groups : [];
    if (groups.includes('SPY_Super_admin') || permissions.includes('argus.kanban.super')) return teams;
    let list = teams.filter(
      (t) => permissions.includes(`argus.kanban.manage.${t}`),
    );
    if (
      limitedTesseract &&
      !limitedTesseract.calendarViewOnly &&
      (limitedTesseract.role === 'buildteam' || limitedTesseract.role === 'eventteam')
    ) {
      list = list.filter((t) => t === limitedTesseract.role);
    }
    return list;
  }, [identity, isDev, teams, limitedTesseract]);

  // Load teams
  useEffect(() => {
    const loadTeams = async () => {
      try {
        const res = await kanbanApiService.listTeams();
        setServerTeams(res.teams);
      } catch (err) {
        console.error('Failed to load workspace teams:', err);
      }
    };
    loadTeams();
  }, []);

  const loadBoards = useCallback(
    async (opts?: { initial?: boolean }) => {
      if (!selectedTeam) return;
      const initial = opts?.initial ?? false;
      if (initial) {
        setIsLoading(true);
        setError(null);
      }
      try {
        if (selectedTeam === ALL_TEAMS_TAB) {
          const [boardsRes, statsResults] = await Promise.all([
            kanbanApiService.listAllBoards({ includeArchived: showArchived, limit: 100 }),
            Promise.all(teams.map((team) => kanbanApiService.getTeamStats({ team }).catch(() => null))),
          ]);
          setBoards(Array.isArray(boardsRes.results) ? boardsRes.results : []);
          setStats(aggregateKanbanStats(statsResults.filter((result): result is KanbanStats => result != null)));
        } else {
          const [boardsRes, statsRes] = await Promise.all([
            kanbanApiService.listBoards({ team: selectedTeam, includeArchived: showArchived, limit: 100 }),
            kanbanApiService.getTeamStats({ team: selectedTeam }).catch(() => null),
          ]);
          setBoards(Array.isArray(boardsRes.results) ? boardsRes.results : []);
          setStats(statsRes);
        }
      } catch (err) {
        if (initial) setError(err instanceof Error ? err.message : 'Failed to load boards');
      } finally {
        if (initial) setIsLoading(false);
      }
    },
    [selectedTeam, showArchived, teams],
  );

  useEffect(() => {
    void loadBoards({ initial: true });
  }, [loadBoards]);

  const handleTeamWs = useCallback(
    (env: KanbanWsEnvelope) => {
      if (env.type.startsWith('system.')) return;
      if (!selectedTeam) return;
      const boardTypes = new Set([
        'board.created',
        'board.updated',
        'board.archived',
        'board.unarchived',
        'board.deleted',
      ]);
      if (!boardTypes.has(env.type)) return;

      const p = env.payload ?? {};
      const rawBoard = p.board;
      const boardTeam =
        rawBoard &&
        typeof rawBoard === 'object' &&
        typeof (rawBoard as { team?: unknown }).team === 'string'
          ? (rawBoard as { team: string }).team
          : null;
      const targetTeam =
        selectedTeam === ALL_TEAMS_TAB
          ? boardTeam ?? (typeof env.team === 'string' && env.team ? env.team : null)
          : selectedTeam;
      if (!targetTeam) return;

      setBoards((prev) => {
        const { next, heal } = mergeTeamBoardsList(prev, targetTeam, env);
        if (heal) {
          queueMicrotask(() => void loadBoards());
          return prev;
        }
        return next;
      });
    },
    [selectedTeam, loadBoards],
  );

  const teamWsEnabled =
    selectedTeam != null &&
    selectedTeam !== ALL_TEAMS_TAB &&
    (isDev || (!authLoading && identity != null && canReadTeam(selectedTeam)));

  useKanbanTeamWebSocket({
    team: selectedTeam,
    enabled: teamWsEnabled,
    onEnvelope: handleTeamWs,
    onSeqGap: () => void loadBoards(),
  });

  useEffect(() => {
    if (!selectedTeam) return;
    const pollMs = 120_000;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') void loadBoards();
    }, pollMs);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void loadBoards();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [selectedTeam, loadBoards]);

  // Load recent activity and upcoming cadences
  useEffect(() => {
    if (!selectedTeam) return;
    const teamArg = selectedTeam === ALL_TEAMS_TAB ? undefined : selectedTeam;
    kanbanApiService.getRecentActivity({ team: teamArg, limit: 10 })
      .then(setRecentActivity).catch(console.error);
    kanbanApiService.getUpcomingCadences({ team: teamArg, limit: 5 })
      .then(setUpcomingCadences).catch(console.error);
  }, [selectedTeam]);

  // Server-side search when search text exists
  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setSearchResults(null);
      return;
    }
    const teamArg = selectedTeam === ALL_TEAMS_TAB || !selectedTeam ? undefined : selectedTeam;
    kanbanApiService.searchBoards({ q: debouncedSearch, team: teamArg })
      .then(res => setSearchResults(Array.isArray(res.results) ? res.results : []))
      .catch(console.error);
  }, [debouncedSearch, selectedTeam]);

  // Quick access: get or create default board for team
  const handleQuickAccess = async (team: string) => {
    try {
      const board = await kanbanApiService.getOrCreateDefaultBoard(team);
      navigate(`/workspaces/${board.id}`);
    } catch (err) {
      console.error('Failed to get default board:', err);
    }
  };

  // Search filtering (client-side fallback when no server search)
  const filteredBoards = useMemo(() => {
    if (!debouncedSearch) return boards;
    const q = debouncedSearch.toLowerCase();
    return boards.filter((b) => {
      const title = (b.title ?? '').toLowerCase();
      const desc = b.description != null ? String(b.description).toLowerCase() : '';
      return title.includes(q) || desc.includes(q);
    });
  }, [boards, debouncedSearch]);

  const handleDeleteBoard = async () => {
    if (!deletingBoardId) return;
    try {
      await kanbanApiService.deleteBoard(deletingBoardId);
      setBoards((prev) => prev.filter((b) => b.id !== deletingBoardId));
      setDeletingBoardId(null);
    } catch (err) {
      console.error('Failed to delete board:', err);
    }
  };

  const handleBoardCreated = (board: KanbanBoard, ctx: BoardCreatedContext) => {
    const normalized = normalizeKanbanBoardFromApiResponse(board);
    const optimistic: KanbanBoard = {
      ...normalized,
      title: normalized.title.trim() !== '' ? normalized.title : ctx.formTitle,
      team: normalized.team.trim() !== '' ? normalized.team : ctx.formTeam,
      created_by: normalized.created_by.trim() !== '' ? normalized.created_by : ctx.creatorUuid,
      description:
        normalized.description != null && String(normalized.description).trim() !== ''
          ? normalized.description
          : ctx.formDescription ?? null,
      background_color:
        normalized.background_color != null && String(normalized.background_color).trim() !== ''
          ? normalized.background_color
          : ctx.formBackground || null,
      created_at: normalized.created_at > 0 ? normalized.created_at : Date.now(),
    };
    // Without a real id, WS `board.created` cannot merge with this row → duplicate cards. Rely on refetch only.
    if (!optimistic.id.trim()) {
      setShowCreateModal(false);
      void loadBoards();
      return;
    }
    setBoards((prev) => {
      const withoutDup = prev.filter((b) => b.id !== optimistic.id);
      return [optimistic, ...withoutDup];
    });
    setShowCreateModal(false);
    queueMicrotask(() => {
      void loadBoards();
    });
  };

  const teamColors: Record<string, string> = {
    devteam: '#3498DB',
    buildteam: '#E67E22',
    eventteam: '#2ECC71',
    event: '#2ECC71',
    management: '#9B59B6',
  };

  const teamLabel = (team: string) =>
    team.replace('team', ' Team').replace(/^\w/, (c) => c.toUpperCase());

  const renderBoardsSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" aria-busy="true">
      {[1, 2, 3].map((i) => (
        <div key={i} className="glass-card p-4 md:p-5 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <SkeletonLoader className="h-4 w-2/3" />
            <SkeletonLoader className="h-5 w-16 rounded-full" />
          </div>
          <SkeletonLoader className="h-3 w-full" />
          <SkeletonLoader className="h-3 w-1/2" />
          <div className="flex items-center justify-between pt-3 border-t border-glass-200/10">
            <SkeletonLoader className="h-3 w-24" />
            <SkeletonLoader className="h-5 w-5 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Planning"
        title="Workspaces"
        description="Kanban boards shared with your staff teams or with you directly."
        icon={KanbanSquare}
        actions={
          canManage ? (
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4" />
              Create Board
            </button>
          ) : undefined
        }
      >
        {/* Toolbar: team chips, search, archived toggle, result count */}
        <div className="flex flex-col gap-2">
          {/* Team Selector */}
          {teams.length > 0 && (
            <div className="segmented" role="tablist" aria-label="Team">
              <button
                type="button"
                role="tab"
                onClick={() => setSelectedTeam(ALL_TEAMS_TAB)}
                className="segmented-item"
                data-active={selectedTeam === ALL_TEAMS_TAB ? 'true' : 'false'}
                aria-selected={selectedTeam === ALL_TEAMS_TAB}
              >
                <span className="inline-block w-2 h-2 rounded-full bg-accent-400" aria-hidden />
                All teams
              </button>
              {teams.map((team) => (
                <button
                  key={team}
                  type="button"
                  role="tab"
                  onClick={() => setSelectedTeam(team)}
                  className="segmented-item"
                  data-active={selectedTeam === team ? 'true' : 'false'}
                  aria-selected={selectedTeam === team}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ backgroundColor: teamColors[team] || '#95A5A6' }}
                    aria-hidden
                  />
                  {teamLabel(team)}
                </button>
              ))}
            </div>
          )}

          {/* Search + Filters */}
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
              <input
                className="glass-input w-full pl-9"
                placeholder="Search boards…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-glass-400 cursor-pointer whitespace-nowrap md:px-1">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              <Archive className="w-4 h-4" />
              Show Archived
            </label>
            {!isLoading && !error && (
              <span className="text-xs text-glass-400 tabular-nums md:ml-auto md:text-right">
                {filteredBoards.length.toLocaleString()} {filteredBoards.length === 1 ? 'board' : 'boards'}
              </span>
            )}
          </div>
        </div>
      </PageHeader>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile label="Total Boards" value={stats.total_boards.toLocaleString()} icon={LayoutGrid} tone="info" />
          <StatTile label="Active Cards" value={stats.total_cards.toLocaleString()} icon={CheckCircle2} tone="success" />
          <StatTile
            label="Blocked"
            value={<span className={cn(stats.cards_blocked > 0 && 'text-red-400')}>{stats.cards_blocked.toLocaleString()}</span>}
            icon={Ban}
            tone={stats.cards_blocked > 0 ? 'danger' : 'neutral'}
          />
          <StatTile
            label="Overdue"
            value={<span className={cn(stats.cards_overdue > 0 && 'text-orange-400')}>{stats.cards_overdue.toLocaleString()}</span>}
            icon={Clock}
            tone={stats.cards_overdue > 0 ? 'orange' : 'neutral'}
          />
        </div>
      )}

      {/* Quick Access + Upcoming Cadences + Recent Activity (progressive disclosure) */}
      <button
        type="button"
        onClick={() => setShowAdvanced(v => !v)}
        className="btn-ghost btn-xs -ml-2"
        aria-expanded={showAdvanced}
      >
        {showAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <span>{showAdvanced ? 'Hide' : 'Show'} Quick Access, Cadences & Activity</span>
      </button>
      {showAdvanced && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Quick Access */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Quick Access" icon={Zap} iconClassName="text-yellow-400" />
          <div className="space-y-2">
            {teams.map(team => (
              <button
                key={team}
                type="button"
                onClick={() => handleQuickAccess(team)}
                className="glass-button-sm w-full justify-start"
              >
                <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: teamColors[team] || '#95A5A6' }} aria-hidden />
                Open {team.replace('team', ' ')} Default Board
              </button>
            ))}
          </div>
        </div>

        {/* Upcoming Cadences */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Upcoming Cadences" icon={CalendarDays} iconClassName="text-blue-400" count={upcomingCadences.length} />
          {upcomingCadences.length === 0 ? (
            <p className="text-xs text-glass-500">No upcoming cadences</p>
          ) : (
            <div className="divide-y divide-glass-200/10">
              {upcomingCadences.map(c => {
                const cfg = CADENCE_TYPE_CONFIG[c.cadence_type];
                return (
                  <Link key={c.id} to={`/workspaces/${c.board_id}`} className="block -mx-2 px-2 py-2 rounded-lg hover:bg-glass-200/10 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cfg?.color || '#95A5A6' }} aria-hidden />
                      <span className="text-sm text-white font-medium truncate">{c.title}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-glass-400">
                      <span className="chip">{cfg?.label}</span>
                      <span className="tabular-nums">{c.next_occurrence ? format(new Date(c.next_occurrence), 'MMM d, h:mm a') : 'Not scheduled'}</span>
                    </div>
                    <p className="text-xs text-glass-500 mt-0.5 truncate">{c.board_title} · {c.team}</p>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Recent Activity" icon={Activity} iconClassName="text-green-400" />
          {recentActivity.length === 0 ? (
            <p className="text-xs text-glass-500">No recent activity</p>
          ) : (
            <div className="divide-y divide-glass-200/10 max-h-48 overflow-y-auto">
              {recentActivity.map(entry => (
                <div key={entry.id} className="flex items-start gap-2 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-glass-300 truncate">{entry.description}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <PlayerProfile uuid={entry.actor_id} inline showUuid={false} />
                      <span className="text-xs text-glass-500 tabular-nums">
                        {entry.created_at > 0
                          ? formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })
                          : 'just now'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Server Search Results */}
      {searchResults && (
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Search Results" icon={Search} count={searchResults.length} />
          {searchResults.length === 0 ? (
            <p className="text-xs text-glass-500">No boards match &quot;{debouncedSearch}&quot;</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {searchResults.map(b => (
                <Link key={b.id} to={`/workspaces/${b.id}`} className="subcard p-3 block hover:border-glass-200/25 transition-colors">
                  <h4 className="text-sm font-semibold text-white truncate">{b.title}</h4>
                  <p className="text-xs text-glass-400">{b.team}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Board Grid */}
      {isLoading ? (
        renderBoardsSkeleton()
      ) : error ? (
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to Load"
          description={error}
          action={
            <button
              type="button"
              onClick={() => void loadBoards({ initial: true })}
              className="glass-button"
            >
              Retry
            </button>
          }
        />
      ) : filteredBoards.length === 0 ? (
        <EmptyState
          icon={debouncedSearch ? Search : KanbanSquare}
          title={debouncedSearch ? 'No boards match your search' : 'No boards yet'}
          description={debouncedSearch ? 'Try a different search term.' : 'Create your first board to get started.'}
          action={
            !debouncedSearch && canManage ? (
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="glass-button"
              >
                <Plus className="w-4 h-4" />
                Create Board
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBoards.map((board) => (
            <Link
              key={board.id}
              to={`/workspaces/${board.id}`}
              className={cn(
                'glass-card glass-card-interactive p-4 md:p-5 group relative block overflow-hidden',
                board.is_archived === 1 && 'opacity-75',
              )}
            >
              {/* Background color accent bar */}
              {board.background_color && (
                <div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ backgroundColor: board.background_color }}
                />
              )}

              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-white truncate group-hover:text-accent-300 transition-colors">
                      {board.title}
                    </h3>
                    {board.is_default === 1 && (
                      <Star className="w-4 h-4 text-yellow-400 flex-shrink-0" aria-label="Default board" />
                    )}
                  </div>
                  {board.is_archived === 1 && (
                    <span className="badge badge-neutral mt-1.5">
                      <Archive className="w-3 h-3" />
                      Archived
                    </span>
                  )}
                </div>
                <span
                  className="badge border flex-shrink-0"
                  style={{
                    backgroundColor: `${teamColors[board.team] || '#95A5A6'}20`,
                    borderColor: `${teamColors[board.team] || '#95A5A6'}40`,
                    color: teamColors[board.team] || '#95A5A6',
                  }}
                >
                  {board.team}
                </span>
              </div>

              {board.description && (
                <p className="text-sm text-glass-300 mb-3 line-clamp-2">
                  {board.description}
                </p>
              )}

              <div className="flex items-center justify-between gap-2 text-xs text-glass-400 pt-3 mt-3 border-t border-glass-200/10">
                <span className="tabular-nums truncate">
                  {board.created_at > 0 ? (
                    <>
                      Created{' '}
                      {formatDistanceToNow(new Date(board.created_at), {
                        addSuffix: true,
                      })}
                    </>
                  ) : (
                    'Created just now'
                  )}
                </span>
                <PlayerProfile uuid={board.created_by} inline showUuid={false} />
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Create Board Modal */}
      {showCreateModal && (
        <CreateBoardModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleBoardCreated}
          teams={manageableTeams}
          defaultTeam={
            selectedTeam && selectedTeam !== ALL_TEAMS_TAB
              ? selectedTeam
              : manageableTeams[0]
          }
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!deletingBoardId}
        onClose={() => setDeletingBoardId(null)}
        onConfirm={handleDeleteBoard}
        title="Delete Board"
        message="This will permanently delete the board and all its lists, cards, labels, and data. This action cannot be undone."
        confirmText="Delete Board"
      />
    </div>
  );
}
